import api from "@lib/api";
import { useToast } from "@lib/hooks/useToast";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

interface BulkActionConfig {
  type: "delete" | "patch" | "post" | "custom";
  endpoint?: string;
  payload?: Record<string, unknown>;
  customFn?: (item: any) => Promise<void>;
  successMessage?: string;
  errorMessage?: string;
  showToast?: boolean;
}

interface UseBulkActionsOptions {
  resource: string;
  queryKey?: string;
  onSuccess?: (results: { success: any[]; error: any[] }) => void;
  onError?: (error: unknown) => void;
}

interface BulkActionResults {
  success: any[];
  error: any[];
}

export function useBulkActions({ resource, queryKey, onSuccess, onError }: UseBulkActionsOptions) {
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<BulkActionResults>({ success: [], error: [] });
  const queryClient = useQueryClient();
  const toast = useToast();

  const executeAction = useCallback(
    async (selectedItems: any[], action: BulkActionConfig) => {
      if (!selectedItems || selectedItems.length === 0) {
        toast.warning("Aucun élément sélectionné");
        return;
      }

      setProgress(0);
      setResults({ success: [], error: [] });

      const total = selectedItems.length;
      const successItems: any[] = [];
      const errorItems: { item: any; error: unknown }[] = [];

      try {
        for (let i = 0; i < selectedItems.length; i++) {
          const item = selectedItems[i];
          const currentProgress = Math.round(((i + 1) / total) * 100);

          try {
            switch (action.type) {
              case "delete":
                await api.delete(`${resource}/${item.id}`);
                break;
              case "patch":
                await api.patch(`${resource}/${item.id}`, action.payload);
                break;
              case "post":
                await api.post(action.endpoint || `${resource}/${item.id}`, action.payload);
                break;
              case "custom":
                if (action.customFn) {
                  await action.customFn(item);
                }
                break;
              default:
                throw new Error(`Unknown action type: ${action.type}`);
            }
            successItems.push(item);
          } catch (error) {
            console.error(`Error processing item ${item.id}:`, error);
            errorItems.push({ item, error });
          }

          setProgress(currentProgress);
        }

        setResults({ success: successItems, error: errorItems });

        if (action.showToast !== false) {
          if (errorItems.length === 0) {
            toast.success(
              action.successMessage || `${successItems.length} élément(s) traité(s) avec succès`,
            );
          } else if (successItems.length === 0) {
            toast.error(
              action.errorMessage || `Erreur lors du traitement de ${errorItems.length} élément(s)`,
            );
          } else {
            toast.warning(`${successItems.length} succès, ${errorItems.length} erreur(s)`);
          }
        }

        if (queryKey) {
          queryClient.invalidateQueries({ queryKey: [queryKey] });
        }

        if (onSuccess) {
          onSuccess({ success: successItems, error: errorItems });
        }

        return { success: successItems, error: errorItems };
      } catch (error) {
        console.error("Bulk action error:", error);
        toast.error("Une erreur est survenue lors de l'opération");

        if (onError) {
          onError(error);
        }

        return { success: successItems, error: errorItems };
      }
    },
    [resource, queryKey, queryClient, toast, onSuccess, onError],
  );

  const executeSimpleAction = useCallback(
    async (
      selectedItems: any[],
      config: Partial<BulkActionConfig> & { method?: string; payload?: Record<string, unknown> },
    ) => {
      return executeAction(selectedItems, {
        type: (config.method as BulkActionConfig["type"]) || "patch",
        payload: config.payload,
        successMessage: config.successMessage,
        errorMessage: config.errorMessage,
        showToast: config.showToast,
      });
    },
    [executeAction],
  );

  return {
    executeAction,
    executeSimpleAction,
    progress,
    results,
    isProcessing: progress > 0 && progress < 100,
  };
}

export default useBulkActions;
